// Parameter-change-vs-last-run diff for the Hedging Grid CoPilot's Adjust + Close tabs
// (CR-20260831-004).
//
// ISOLATED SIBLING of copilot-shared/opg-copilot-param-diff.ts and the APG twin — the SHAPE is
// duplicated, never imported across panel folders (family rule). Compares the LIVE tab state
// against the baseline the current Run captured (Run.sentInputs) and returns a flat map of
// CHANGED-FIELD KEYS driving the yellow "this no longer matches the shown results" cues. The
// Select tab needs nothing here: it is the embedded OPG copilot, which carries its own diff.
//
// Key grammar — Adjust:
//   expiration                                    — the P&L-line expiration chip
//   priceRange.kind                               — Range | Fill Gap | Follow-Through | Wall
//   startStrike / endStrike                       — the bounded/fill-gap strike pair
//   useExpectedRange                              — the Range form's "Adjust to Expected Range" box
//   ft.up.enabled / ft.up.startStrike / ft.up.minProfit / ft.up.minProfitMode / ft.up.harvest
//   ft.down.*                                     — same, down side
//   ft.closeRemaining
//   minProfit.amount / minProfit.mode             — the global minimum (non-FT forms)
//   maxRisk.kind / maxRisk.amount / maxRisk.normalized / maxRisk.dollarLevel / maxRisk.level
//   harvest / riskSide / halfRisk
// Close:
//   dates / target.kind / target.hedges
//
// Union semantics: a differing KIND flags only the kind key (the newly selected toggle paints
// yellow); leaves are compared only while the kinds match. A follow-through side flipping between
// null and enabled flags only its enable checkbox. NaN-vs-NaN is EQUAL (both mean "empty box").
//
// The diff runs over the EMITTED state objects, which gate hidden controls to defaults
// (buildState) — so an edit on a control the form is not currently showing never flags, matching
// what actually travels on a run.
//
// ES5 note: arrays + indexOf only (no Set/Map iteration, no for..of), matching the family.

import {
    AdjustInputs,
    CloseInputs,
    FollowThroughSideInput,
    MaxRiskCeiling,
    PriceRange
} from './copilot-domain';

export type ParamChangeMap = { [key: string]: boolean };

export function hasAnyChange(map: ParamChangeMap | null): boolean {
    if (!map) {
        return false;
    }
    for (const k in map) {
        if (map.hasOwnProperty(k)) {
            return true;
        }
    }
    return false;
}

// ---- Adjust -------------------------------------------------------------------------------

export function diffAdjustInputs(baseline: AdjustInputs | null, live: AdjustInputs | null): ParamChangeMap {
    const out: ParamChangeMap = {};
    if (!baseline || !live) {
        return out;
    }
    if (!sameDate(baseline.expiration, live.expiration)) {
        out['expiration'] = true;
    }
    diffPriceRange(baseline.priceRange, live.priceRange, out);
    if (!numEq(baseline.minProfit, live.minProfit)) {
        out['minProfit.amount'] = true;
    }
    if (!!baseline.minProfitNormalized !== !!live.minProfitNormalized) {
        out['minProfit.mode'] = true;
    }
    diffMaxRisk(baseline.maxRisk, live.maxRisk, out);
    if (!!baseline.harvest !== !!live.harvest) {
        out['harvest'] = true;
    }
    if (baseline.riskSide !== live.riskSide) {
        out['riskSide'] = true;
    }
    if (!!baseline.halfRisk !== !!live.halfRisk) {
        out['halfRisk'] = true;
    }
    return out;
}

function diffPriceRange(base: PriceRange, live: PriceRange, out: ParamChangeMap): void {
    if (!base || !live) {
        return;
    }
    if (base.kind !== live.kind) {
        out['priceRange.kind'] = true;
        return;
    }
    if ((base.kind === 'bounded' && live.kind === 'bounded')
        || (base.kind === 'fillGap' && live.kind === 'fillGap')) {
        if (base.kind === 'bounded' && live.kind === 'bounded'
            && !!base.useExpectedRange !== !!live.useExpectedRange) {
            out['useExpectedRange'] = true;
            // The flip owns the strike story too (ticking it clears + disables the pair) — the
            // checkbox cue is the whole change, so the pair is not additionally compared.
            return;
        }
        if (!numEq(base.startStrike, live.startStrike)) {
            out['startStrike'] = true;
        }
        if (!numEq(base.endStrike, live.endStrike)) {
            out['endStrike'] = true;
        }
    } else if (base.kind === 'followThrough' && live.kind === 'followThrough') {
        diffFtSide(base.up, live.up, 'ft.up.', out);
        diffFtSide(base.down, live.down, 'ft.down.', out);
        if (!!base.closeRemaining !== !!live.closeRemaining) {
            out['ft.closeRemaining'] = true;
        }
    }
    // 'wall' carries no fields — same kind means same selection.
}

function diffFtSide(base: FollowThroughSideInput | null, live: FollowThroughSideInput | null,
                    prefix: string, out: ParamChangeMap): void {
    if (!base && !live) {
        return;
    }
    if (!base || !live) {
        // Enabled flipped — the side's enable checkbox is the change; a disabled side's kept-but-
        // inert values are not compared (they do not travel).
        out[prefix + 'enabled'] = true;
        return;
    }
    if (!numEq(base.startStrike, live.startStrike)) {
        out[prefix + 'startStrike'] = true;
    }
    if (!numEq(base.minProfit, live.minProfit)) {
        out[prefix + 'minProfit'] = true;
    }
    if (!!base.minProfitNormalized !== !!live.minProfitNormalized) {
        out[prefix + 'minProfitMode'] = true;
    }
    if (!!base.harvest !== !!live.harvest) {
        out[prefix + 'harvest'] = true;
    }
}

function diffMaxRisk(base: MaxRiskCeiling, live: MaxRiskCeiling, out: ParamChangeMap): void {
    if (!base || !live) {
        return;
    }
    if (base.kind !== live.kind) {
        out['maxRisk.kind'] = true;
        return;
    }
    if (base.kind === 'dollar' && live.kind === 'dollar') {
        if (!numEq(base.amount, live.amount)) {
            out['maxRisk.amount'] = true;
        }
        if (!!base.normalized !== !!live.normalized) {
            out['maxRisk.normalized'] = true;
        }
        if (base.level !== live.level) {
            out['maxRisk.dollarLevel'] = true;
        }
    } else if (base.kind === 'relative' && live.kind === 'relative') {
        if (base.level !== live.level) {
            out['maxRisk.level'] = true;
        }
    }
}

// ---- Close --------------------------------------------------------------------------------

export function diffCloseInputs(baseline: CloseInputs | null, live: CloseInputs | null): ParamChangeMap {
    const out: ParamChangeMap = {};
    if (!baseline || !live) {
        return out;
    }
    if (!sameDateSet(baseline.dates, live.dates)) {
        out['dates'] = true;
    }
    const bt = baseline.target;
    const lt = live.target;
    if (bt && lt) {
        if (bt.kind !== lt.kind) {
            out['target.kind'] = true;
        } else if (bt.kind === 'hedges' && lt.kind === 'hedges'
            && !sameStringSet(bt.hedgeIds, lt.hedgeIds)) {
            out['target.hedges'] = true;
        }
    }
    return out;
}

// ---- Run-fire snapshots -------------------------------------------------------------------
// The questionnaires build a fresh object per emit, but nothing ENFORCES that the reference the
// session keeps is never mutated — the baseline gets its own copy ("snapshot of what was sent").

export function cloneAdjustInputs(i: AdjustInputs): AdjustInputs {
    return {
        expiration: cloneDate(i.expiration),
        priceRange: clonePriceRange(i.priceRange),
        expectedRangeModel: i.expectedRangeModel
            ? { model: i.expectedRangeModel.model, customPercent: i.expectedRangeModel.customPercent }
            : i.expectedRangeModel,
        minProfit: i.minProfit,
        minProfitNormalized: i.minProfitNormalized,
        maxRisk: cloneMaxRisk(i.maxRisk),
        harvest: i.harvest,
        riskSide: i.riskSide,
        halfRisk: i.halfRisk
    };
}

export function cloneCloseInputs(i: CloseInputs): CloseInputs {
    return {
        dates: (i.dates || []).map(d => cloneDate(d) as Date),
        target: i.target.kind === 'all'
            ? { kind: 'all' }
            : { kind: 'hedges', hedgeIds: i.target.hedgeIds.slice() }
    };
}

function clonePriceRange(pr: PriceRange): PriceRange {
    if (!pr) {
        return pr;
    }
    if (pr.kind === 'bounded') {
        return { kind: 'bounded', startStrike: pr.startStrike, endStrike: pr.endStrike, useExpectedRange: pr.useExpectedRange };
    }
    if (pr.kind === 'fillGap') {
        return { kind: 'fillGap', startStrike: pr.startStrike, endStrike: pr.endStrike };
    }
    if (pr.kind === 'followThrough') {
        return {
            kind: 'followThrough',
            up: cloneFtSide(pr.up),
            down: cloneFtSide(pr.down),
            closeRemaining: pr.closeRemaining
        };
    }
    return { kind: 'wall' };
}

function cloneFtSide(s: FollowThroughSideInput | null): FollowThroughSideInput | null {
    if (!s) {
        return null;
    }
    return { startStrike: s.startStrike, minProfit: s.minProfit, minProfitNormalized: s.minProfitNormalized, harvest: s.harvest };
}

function cloneMaxRisk(mr: MaxRiskCeiling): MaxRiskCeiling {
    if (!mr) {
        return mr;
    }
    if (mr.kind === 'dollar') {
        return { kind: 'dollar', amount: mr.amount, normalized: mr.normalized, level: mr.level };
    }
    return { kind: 'relative', level: mr.level };
}

// ---- primitives ---------------------------------------------------------------------------

function cloneDate(d: Date | null): Date | null {
    return d instanceof Date ? new Date(d.getTime()) : null;
}

// NaN means "empty box" here (the dropdown/number-box empty state), so NaN equals NaN.
function numEq(a: number, b: number): boolean {
    const aNan = typeof a !== 'number' || isNaN(a);
    const bNan = typeof b !== 'number' || isNaN(b);
    if (aNan || bNan) {
        return aNan === bNan;
    }
    return a === b;
}

function sameDate(x: Date | null, y: Date | null): boolean {
    const xv = x instanceof Date && !isNaN(x.getTime());
    const yv = y instanceof Date && !isNaN(y.getTime());
    if (!xv || !yv) {
        return xv === yv;
    }
    return (x as Date).getTime() === (y as Date).getTime();
}

// Order-insensitive set compares: re-picking the same dates/hedges in another order is no change.
function sameDateSet(a: Date[], b: Date[]): boolean {
    const at = validTimes(a);
    const bt = validTimes(b);
    if (at.length !== bt.length) {
        return false;
    }
    for (let i = 0; i < at.length; i++) {
        if (bt.indexOf(at[i]) < 0) {
            return false;
        }
    }
    return true;
}

function sameStringSet(a: string[], b: string[]): boolean {
    if (!a || !b) {
        return (!a || a.length === 0) === (!b || b.length === 0);
    }
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (b.indexOf(a[i]) < 0) {
            return false;
        }
    }
    return true;
}

function validTimes(list: Date[]): number[] {
    const out: number[] = [];
    if (!list) {
        return out;
    }
    for (let i = 0; i < list.length; i++) {
        const d = list[i];
        if (d instanceof Date && !isNaN(d.getTime()) && out.indexOf(d.getTime()) < 0) {
            out.push(d.getTime());
        }
    }
    return out;
}
